/* Peptide Potion — motion layer
   Smooth scrolling (Lenis) + scroll-triggered choreography (GSAP/ScrollTrigger).
   Defensive by design: if either library fails to load, or the visitor prefers
   reduced motion, the page behaves like a normal static site — nothing is ever
   hidden without JS animating it back in. */

(function () {
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Respect prefers-reduced-motion for the autoplay product video. The <video>
  // tag's `autoplay` attribute is honored by the browser regardless of JS, so we
  // pause it here and keep it paused (poster stays visible). This runs even if
  // GSAP/Lenis never load, since the reduced-motion init() below bails early.
  function pauseVideoForReducedMotion() {
    if (!prefersReducedMotion) return;
    var video = document.querySelector('.orly-video__el');
    if (!video) return;
    var hold = function () { try { video.pause(); } catch (e) {} };
    hold();
    // Some browsers begin autoplay slightly after load; keep it pinned to the
    // poster frame and stop it from resuming on its own.
    video.addEventListener('play', hold);
    video.addEventListener('loadeddata', hold);
    window.addEventListener('load', hold);
  }

  function init() {
    if (prefersReducedMotion) return;
    if (typeof gsap === 'undefined') return;

    document.documentElement.classList.add('orly-motion-ready');

    // Touch devices (phones/tablets): Lenis does NOT drive touch scrolling, so
    // wiring ScrollTrigger updates to Lenis there means reveal triggers never
    // fire and every animated-in section stays stuck at opacity:0 (the mobile
    // "white gaps / can't scroll past" bug). On touch we skip Lenis entirely and
    // let ScrollTrigger use the browser's native scroll, which works correctly.
    var isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    // ---- Smooth scroll (Lenis) — desktop/pointer only ----
    var lenis;
    if (typeof Lenis !== 'undefined' && !isTouch) {
      lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);
    }

    var hasST = typeof ScrollTrigger !== 'undefined';
    if (hasST) {
      gsap.registerPlugin(ScrollTrigger);
      if (lenis) lenis.on('scroll', ScrollTrigger.update);
      // Recompute trigger positions once images/fonts have loaded and laid out,
      // so nothing reveals at the wrong scroll position (or never reveals).
      window.addEventListener('load', function () { ScrollTrigger.refresh(); });
    }

    // ---- Smooth-scroll nav anchors through Lenis ----
    // Links look like "/#science" so they also work from other pages
    // (full navigation there, smooth scroll when already on the page).
    document.querySelectorAll('[data-orly-scroll]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var href = link.getAttribute('href') || '';
        var hashIndex = href.indexOf('#');
        if (hashIndex === -1) return;
        var path = href.slice(0, hashIndex).replace(/\/$/, '');
        var here = window.location.pathname.replace(/\/$/, '');
        if (path && path !== here) return;
        var target = document.querySelector(href.slice(hashIndex));
        if (!target) return;
        e.preventDefault();
        if (lenis) lenis.scrollTo(target, { offset: -90 });
        else target.scrollIntoView({ behavior: 'smooth' });
      });
    });

    // ---- Hero entrance (runs on load) — stagger the copy + settle the bottle in ----
    var heroContent = document.querySelectorAll('.orly-hero__content > *');
    if (heroContent.length) {
      var htl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      htl.from(heroContent, { opacity: 0, y: 36, duration: 1.1, stagger: 0.13 }, 0.15);
      var heroBottle = document.querySelector('.orly-hero__bottle');
      if (heroBottle) htl.from(heroBottle, { opacity: 0, scale: 1.05, duration: 1.7 }, 0.05);
    }

    if (!hasST) return;

    // ---- Parallax (decorative depth on scroll) ----
    document.querySelectorAll('[data-orly-parallax]').forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-parallax-strength')) || 30;
      gsap.to(el, {
        yPercent: function () { return strength / 10; },
        y: strength,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });

    // ---- Card grids: stagger columns in as a group ----
    document.querySelectorAll('.multicolumn-list').forEach(function (list) {
      gsap.from(list.children, {
        opacity: 0, y: 32, duration: 0.85, ease: 'power3.out', stagger: 0.12,
        scrollTrigger: { trigger: list, start: 'top 82%', once: true },
      });
    });

    // ---- Single content blocks rise in ----
    var singles = document.querySelectorAll(
      '.orly-editorial__media, .orly-editorial__content, .rich-text__blocks, .collapsible-content__header, .newsletter__wrapper, [data-orly-reveal]'
    );
    singles.forEach(function (el) {
      gsap.from(el, {
        opacity: 0, y: 56, duration: 1.0, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 88%', once: true },
      });
    });

    // ---- Staggered children inside a reveal group ----
    document.querySelectorAll('[data-orly-reveal-stagger]').forEach(function (group) {
      gsap.from(group.children, {
        opacity: 0, y: 50, duration: 0.9, ease: 'power3.out', stagger: 0.12,
        scrollTrigger: { trigger: group, start: 'top 86%', once: true },
      });
    });

    // ---- Heading accent underlines wipe in on scroll (data-orly-underline) ----
    document.querySelectorAll('[data-orly-underline]').forEach(function (el) {
      gsap.fromTo(el, { '--ul': 0 }, {
        '--ul': 1, duration: 0.7, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 90%', once: true },
      });
    });

    // ---- Editorial images get a slow drift for depth ----
    document.querySelectorAll('.orly-editorial__media img, [data-orly-kenburns]').forEach(function (img) {
      gsap.fromTo(img, { scale: 1.16 }, {
        scale: 1, ease: 'none',
        scrollTrigger: { trigger: img, start: 'top bottom', end: 'center center', scrub: true },
      });
    });

    // ---- Number count-ups ----
    document.querySelectorAll('[data-orly-count]').forEach(function (el) {
      var raw = el.getAttribute('data-orly-count');
      var target = parseFloat(raw);
      if (isNaN(target)) return;
      var decimals = (raw.split('.')[1] || '').length;
      var prefix = el.getAttribute('data-count-prefix') || '';
      var suffix = el.getAttribute('data-count-suffix') || '';
      var obj = { v: 0 };
      gsap.to(obj, {
        v: target, duration: 1.8, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 88%', once: true },
        onUpdate: function () { el.textContent = prefix + obj.v.toFixed(decimals) + suffix; },
        onComplete: function () { el.textContent = prefix + target.toFixed(decimals) + suffix; },
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      pauseVideoForReducedMotion();
      init();
    });
  } else {
    pauseVideoForReducedMotion();
    init();
  }
})();
